<template>
  <div>
    <Navbar activeDirectory="admin" activePage="admin-interventi"></Navbar>
    <div class="container-fluid py-4">

      <!-- ===== PAGE HEADER ===== -->
      <div class="d-flex align-items-center justify-content-between flex-wrap mb-4" style="gap:12px;">
        <div class="d-flex align-items-center">
          <div class="au-header-icon mr-3">
            <i class="mdi mdi-wrench-outline"></i>
          </div>
          <div class="text-left">
            <h3 class="mb-0 font-weight-bold text-dark">
              {{ isDeveloper ? 'Catalogo Globale Interventi' : 'Tipologie Intervento Aziendali' }}
            </h3>
            <p class="mb-0 text-muted small">
              {{ isDeveloper
                ? 'Gestisci le tipologie globali del sistema (catalogo master)'
                : 'Visualizza le tipologie di intervento abilitate per la tua azienda'
              }}
            </p>
          </div>
        </div>
        <button
          v-if="mode === 'read' && isDeveloper"
          class="au-btn-submit"
          @click="setAddMode"
        >
          <i class="mdi mdi-plus mr-1"></i> Aggiungi Nuova
        </button>
        <button v-else-if="mode === 'add'" class="btn btn-light rounded-pill px-4 font-weight-semibold border" @click="mode = 'read'">
          <i class="mdi mdi-arrow-left mr-1"></i> Torna all'elenco
        </button>
      </div>

      <!-- === MODALITÀ SVILUPPATORE (D): catalogo globale === -->
      <template v-if="isDeveloper">

        <!-- LIST MODE -->
        <div v-if="mode === 'read'">
          <div class="au-card">
            <div class="cw-section-title">Catalogo Interventi</div>
            <div class="mb-4" style="max-width:360px;">
              <div class="au-search-wrap">
                <i class="mdi mdi-magnify au-search-icon"></i>
                <input
                  v-model="searchQuery"
                  class="au-search-input"
                  placeholder="Cerca intervento..."
                >
              </div>
            </div>

            <div class="table-responsive">
              <table class="au-table">
                <thead>
                  <tr>
                    <th style="width: 80px;">ID</th>
                    <th>Nome Intervento</th>
                    <th>Codice Intervento</th>
                    <th>Modulo</th>
                    <th class="text-right" style="width: 120px;">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="5" class="text-center py-4">
                      <span class="spinner-border spinner-border-sm text-primary mr-2" role="status"></span>
                      Caricamento tipologie...
                    </td>
                  </tr>
                  <tr v-else-if="filteredInterventi.length === 0">
                    <td colspan="5" class="text-center py-4 text-muted">
                      <i class="mdi mdi-layers-off d-block mb-2" style="font-size: 2rem; opacity:0.3;"></i>
                      Nessuna tipologia trovata.
                    </td>
                  </tr>
                  <tr v-for="item in filteredInterventi" :key="item.id" v-else>
                    <td class="font-weight-bold text-muted">{{ item.id }}</td>
                    <td><span class="font-weight-bold text-dark">{{ item.nome }}</span></td>
                    <td><code class="text-primary">{{ item.codice }}</code></td>
                    <td>
                      <span v-if="item.module_name" class="au-company-badge text-primary" style="background:#FEF1E0;">
                        {{ item.module_name }}
                      </span>
                      <span v-else class="text-muted small font-italic">—</span>
                    </td>
                    <td class="text-right text-nowrap">
                      <button
                        class="btn btn-sm btn-outline-danger rounded-pill px-3 font-weight-semibold"
                        @click="deleteIntervento(item)"
                        :disabled="isDefaultIntervento(item.codice)"
                        v-tooltip="isDefaultIntervento(item.codice) ? 'Impossibile eliminare gli interventi predefiniti' : 'Elimina intervento'"
                      >
                        Elimina
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- ADD FORM MODE (D) -->
        <div class="row justify-content-center" v-if="mode === 'add'">
          <div class="col-lg-6 col-md-8 mb-4">
            <div class="au-card h-100">
              <div class="cw-section-title">
                <span class="cw-step-num">1</span>Nuova Tipologia Globale
              </div>
              <form @submit.prevent="saveIntervento">
                <div class="form-group mb-4 text-left">
                  <label class="cw-label">Modulo <span class="text-danger">*</span></label>
                  <div class="cw-select-wrap">
                    <i class="mdi mdi-layers-outline cw-field-icon"></i>
                    <select v-model="form.fk_module" class="cw-select" required>
                      <option value="" disabled>Seleziona modulo...</option>
                      <option v-for="mod in modules" :key="mod.id" :value="mod.id">{{ mod.nome }}</option>
                    </select>
                    <i class="mdi mdi-chevron-down cw-select-arrow"></i>
                  </div>
                </div>
                <div class="form-group mb-4 text-left">
                  <label class="cw-label">Nome Tipologia <span class="text-danger">*</span></label>
                  <div class="cw-input-wrap">
                    <i class="mdi mdi-tag-text-outline cw-field-icon"></i>
                    <input v-model="form.nome" class="cw-input" placeholder="Es. Controllo Guardrail" required @input="generateCodice">
                  </div>
                </div>
                <div class="form-group mb-4 text-left">
                  <label class="cw-label">Codice (Autogenerato) <span class="text-danger">*</span></label>
                  <div class="cw-input-wrap">
                    <i class="mdi mdi-code-tags cw-field-icon"></i>
                    <input v-model="form.codice" class="cw-input" style="background:#f8fafc;" required readonly>
                  </div>
                  <small class="text-muted d-block mt-2">Generato automaticamente. Usato internamente per identificare l'intervento.</small>
                </div>
                
                <div class="cw-footer mt-4 d-flex align-items-center justify-content-between flex-wrap">
                  <small class="text-muted">
                    <i class="mdi mdi-asterisk text-danger" style="font-size:0.6rem;"></i> Campi obbligatori
                  </small>
                  <div class="d-flex" style="gap:12px;">
                    <button type="button" class="btn btn-light rounded-pill px-4 font-weight-semibold border" @click="mode = 'read'">Annulla</button>
                    <button type="submit" class="cw-btn-submit" :disabled="submitting">
                      <span class="spinner-border spinner-border-sm mr-2" v-if="submitting"></span>
                      <i class="mdi mdi-check-circle-outline mr-1" v-else></i>
                      Salva
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

      </template>

      <!-- === MODALITÀ AZIENDA (B) === -->
      <template v-else>

        <!-- INFO BANNER -->
        <div class="au-banner-info">
          <i class="mdi mdi-domain au-banner-icon"></i>
          <div class="text-left">
            <strong class="au-banner-title">Tipologie della tua azienda</strong>
            <span class="au-banner-text">
              Le tipologie elencate qui sono disponibili agli operatori della tua azienda. Queste tipologie sono abilitate e configurate per conto dell'azienda da parte dello sviluppatore del sistema.
            </span>
          </div>
        </div>

        <!-- LIST MODE (B) -->
        <div v-if="mode === 'read'">
          <div class="au-card">
            <div class="cw-section-title">Elenco Tipologie Abilitate</div>
            <div class="mb-4" style="max-width:360px;">
              <div class="au-search-wrap">
                <i class="mdi mdi-magnify au-search-icon"></i>
                <input
                  v-model="searchQuery"
                  class="au-search-input"
                  placeholder="Cerca tipologia..."
                >
              </div>
            </div>

            <div class="table-responsive">
              <table class="au-table">
                <thead>
                  <tr>
                    <th style="width: 80px;">ID</th>
                    <th>Nome</th>
                    <th>Codice</th>
                    <th class="text-right" style="width: 150px;">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="4" class="text-center py-4">
                      <span class="spinner-border spinner-border-sm text-primary mr-2"></span>
                      Caricamento...
                    </td>
                  </tr>
                  <tr v-else-if="filteredInterventi.length === 0">
                    <td colspan="4" class="text-center py-4 text-muted">
                      <i class="mdi mdi-layers-off d-block mb-2" style="font-size: 2rem; opacity:0.3;"></i>
                      Nessuna tipologia configurata per la tua azienda.
                    </td>
                  </tr>
                  <tr v-for="item in filteredInterventi" :key="item.id" v-else>
                    <td class="font-weight-bold text-muted">{{ item.id }}</td>
                    <td><span class="font-weight-bold text-dark">{{ item.nome }}</span></td>
                    <td><code class="text-primary">{{ item.codice }}</code></td>
                    <td class="text-right text-nowrap">
                      <button
                        class="btn btn-sm btn-outline-danger rounded-pill px-3 font-weight-semibold"
                        @click="deleteCompanyIntervento(item)"
                      >
                        Disattiva
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- ADD FORM MODE (B) -->
        <div class="row justify-content-center" v-if="mode === 'add'">
          <div class="col-lg-6 col-md-8 mb-4">
            <div class="au-card h-100">
              <div class="cw-section-title">
                <span class="cw-step-num">1</span>Nuova Tipologia Aziendale
              </div>
              <form @submit.prevent="saveCompanyIntervento">
                <div class="form-group mb-4 text-left">
                  <label class="cw-label">Modulo <span class="text-danger">*</span></label>
                  <div class="cw-select-wrap">
                    <i class="mdi mdi-layers-outline cw-field-icon"></i>
                    <select v-model="form.fk_module" class="cw-select" required>
                      <option value="" disabled>Seleziona modulo...</option>
                      <option v-for="mod in modules" :key="mod.id" :value="mod.id">{{ mod.nome }}</option>
                    </select>
                    <i class="mdi mdi-chevron-down cw-select-arrow"></i>
                  </div>
                </div>
                <div class="form-group mb-4 text-left">
                  <label class="cw-label">Nome Tipologia <span class="text-danger">*</span></label>
                  <div class="cw-input-wrap">
                    <i class="mdi mdi-tag-text-outline cw-field-icon"></i>
                    <input v-model="form.nome" class="cw-input" placeholder="Es. Controllo Guardrail" required @input="generateCodice">
                  </div>
                </div>
                <div class="form-group mb-4 text-left">
                  <label class="cw-label">Codice (Autogenerato) <span class="text-danger">*</span></label>
                  <div class="cw-input-wrap">
                    <i class="mdi mdi-code-tags cw-field-icon"></i>
                    <input v-model="form.codice" class="cw-input" style="background:#f8fafc;" required readonly>
                  </div>
                  <small class="text-muted d-block mt-2">Generato automaticamente. Usato internamente per identificare l'intervento.</small>
                </div>
                
                <div class="cw-footer mt-4 d-flex align-items-center justify-content-between flex-wrap">
                  <small class="text-muted">
                    <i class="mdi mdi-asterisk text-danger" style="font-size:0.6rem;"></i> Campi obbligatori
                  </small>
                  <div class="d-flex" style="gap:12px;">
                    <button type="button" class="btn btn-light rounded-pill px-4 font-weight-semibold border" @click="mode = 'read'">Annulla</button>
                    <button type="submit" class="cw-btn-submit" :disabled="submitting">
                      <span class="spinner-border spinner-border-sm mr-2" v-if="submitting"></span>
                      <i class="mdi mdi-check-circle-outline mr-1" v-else></i>
                      Salva
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

      </template>

    </div>
  </div>
</template>

<script>
import { Navbar } from "@/components";

export default {
  components: { Navbar },

  data() {
    return {
      tipologia: localStorage.getItem("tipologia"),
      myCompanyId: parseInt(localStorage.getItem("fk_company")) || null,
      interventi: [],
      modules: [],
      loading: false,
      submitting: false,
      searchQuery: "",
      mode: "read",
      form: { nome: "", codice: "", fk_module: "" },
      defaultInterventi: [
        'buca', 'sfalcio', 'segnaletica', 'pulizia',
        'rifiuto', 'rifiuto_speciale', 'emergenza_carreggiata'
      ]
    };
  },

  computed: {
    isDeveloper() {
      return this.tipologia === "D";
    },

    filteredInterventi() {
      if (!this.searchQuery) return this.interventi;
      const term = this.searchQuery.toLowerCase();
      return this.interventi.filter(
        (i) => i.nome.toLowerCase().includes(term) || i.codice.toLowerCase().includes(term)
      );
    }
  },

  mounted() {
    if (this.tipologia !== 'D') {
      this.$router.push({ name: 'home' });
      return;
    }
    this.fetchInterventi();
    if (this.isDeveloper) {
      this.fetchModules();
    }
  },

  methods: {
    fetchInterventi() {
      this.loading = true;
      const url = this.isDeveloper
        ? '/tipologie-intervento'
        : `/companies/${this.myCompanyId}/interventions`;

      this.axios.get(url)
        .then((response) => {
          this.interventi = Array.isArray(response.data) ? response.data : [];
        })
        .catch((error) => {
          console.error("Errore recupero interventi:", error);
          this.$swal.fire("Errore", "Impossibile caricare le tipologie.", "error");
        })
        .finally(() => { this.loading = false; });
    },

    setAddMode() {
      this.form.nome = "";
      this.form.codice = "";
      this.form.fk_module = "";
      this.mode = "add";
    },

    fetchModules() {
      const url = this.isDeveloper
        ? '/modules'
        : `/companies/${this.myCompanyId}/modules`;

      this.axios.get(url)
        .then((response) => {
          this.modules = Array.isArray(response.data) ? response.data : [];
        })
        .catch((error) => {
          console.error("Errore recupero moduli:", error);
        });
    },

    isDefaultIntervento(codice) {
      return this.defaultInterventi.includes(codice);
    },

    generateCodice() {
      if (!this.form.nome) { this.form.codice = ""; return; }
      this.form.codice = this.form.nome
        .toLowerCase().trim()
        .replace(/[\s-]+/g, "_")
        .replace(/[^\w]/g, "");
    },

    // === D: salva nel catalogo globale ===
    saveIntervento() {
      if (!this.form.nome || !this.form.codice) {
        this.$swal.fire("Attenzione", "Tutti i campi sono obbligatori.", "warning");
        return;
      }
      this.submitting = true;
      const formData = new FormData();
      formData.append("nome", this.form.nome);
      formData.append("codice", this.form.codice);
      formData.append("fk_module", this.form.fk_module);

      this.axios.post('/tipologie-intervento', formData)
        .then((response) => {
          if (response.data && response.data.success) {
            this.$swal.fire("Salvato!", "Tipologia globale aggiunta.", "success");
            this.mode = "read";
            this.fetchInterventi();
          } else {
            this.$swal.fire("Errore", response.data.error || "Impossibile salvare.", "error");
          }
        })
        .catch((error) => {
          console.error("Errore salvataggio:", error);
          this.$swal.fire("Errore", "Impossibile contattare il server.", "error");
        })
        .finally(() => { this.submitting = false; });
    },

    deleteIntervento(item) {
      if (this.isDefaultIntervento(item.codice)) return;
      this.$swal.fire({
        title: "Elimina tipologia?",
        text: `Eliminare "${item.nome}" dal catalogo globale?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sì, elimina!",
        cancelButtonText: "Annulla"
      }).then((result) => {
        if (result.value) {
          this.axios.delete(`/tipologie-intervento/${item.id}`)
            .then((response) => {
              if (response.data && response.data.success) {
                this.$swal.fire("Eliminato!", "Tipologia rimossa dal catalogo.", "success");
                this.fetchInterventi();
              } else {
                this.$swal.fire("Errore", response.data.error || "Impossibile eliminare.", "error");
              }
            })
            .catch(() => {
              this.$swal.fire("Errore", "Si è verificato un errore.", "error");
            });
        }
      });
    },

    // === B: salva nella company tipologia ===
    saveCompanyIntervento() {
      if (!this.form.nome || !this.form.codice) {
        this.$swal.fire("Attenzione", "Tutti i campi sono obbligatori.", "warning");
        return;
      }
      this.submitting = true;
      this.axios.post(`/companies/${this.myCompanyId}/interventions`, {
        nome: this.form.nome,
        codice: this.form.codice,
        fk_module: this.form.fk_module
      })
        .then((response) => {
          if (response.data && response.data.success) {
            this.$swal.fire("Salvato!", "Tipologia aziendale aggiunta con successo.", "success");
            this.mode = "read";
            this.fetchInterventi();
          } else {
            this.$swal.fire("Errore", response.data.error || "Impossibile salvare.", "error");
          }
        })
        .catch(() => {
          this.$swal.fire("Errore", "Impossibile contattare il server.", "error");
        })
        .finally(() => { this.submitting = false; });
    },

    deleteCompanyIntervento(item) {
      this.$swal.fire({
        title: "Disattivare tipologia?",
        text: `La tipologia "${item.nome}" verrà disattivata. Lo storico lavori non sarà perso.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sì, disattiva!",
        cancelButtonText: "Annulla"
      }).then((result) => {
        if (result.value) {
          this.axios.delete(`/companies/${this.myCompanyId}/interventions/${item.id}`)
            .then((response) => {
              if (response.data && response.data.success) {
                this.$swal.fire("Disattivata!", "La tipologia non sarà più disponibile agli operatori.", "success");
                this.fetchInterventi();
              } else {
                this.$swal.fire("Errore", response.data.error || "Impossibile disattivare.", "error");
              }
            })
            .catch(() => {
              this.$swal.fire("Errore", "Si è verificato un errore.", "error");
            });
        }
      });
    }
  }
};
</script>

<style scoped>
/* =========================================
   ADMIN INTERVENTI — Premium UI (RoadME DS)
   ========================================= */

/* ── Shared tokens from style.md ── */
.cw-section-title {
  font-size: 0.82rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.08em;
  color: #64748b; margin-bottom: 22px;
  display: flex; align-items: center;
}
.cw-step-num {
  width: 22px; height: 22px; border-radius: 50%;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  color: #fff; font-size: 0.7rem; font-weight: 800;
  display: inline-flex; align-items: center; justify-content: center;
  margin-right: 10px; flex-shrink: 0;
}
.cw-label {
  font-size: 0.75rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.07em;
  color: #94a3b8; margin-bottom: 8px; display: block;
}
.cw-input-wrap { position: relative; display: flex; align-items: center; }
.cw-field-icon {
  position: absolute; left: 12px;
  color: #94a3b8; font-size: 1rem; z-index: 2; pointer-events: none;
}
.cw-input {
  width: 100%; height: 46px; padding: 0 14px 0 36px;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  background: #f8fafc; font-size: 0.92rem; font-weight: 500; color: #1e293b;
  transition: border-color 0.2s, box-shadow 0.2s; outline: none;
}
.cw-input:focus {
  border-color: #F7931E; box-shadow: 0 0 0 3px rgba(247,147,30,0.12); background: #fff;
}
.cw-select-wrap { position: relative; display: flex; align-items: center; }
.cw-select {
  width: 100%; height: 46px; padding: 0 38px 0 36px;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  background: #f8fafc; appearance: none; -webkit-appearance: none;
  font-size: 0.92rem; font-weight: 500; color: #1e293b;
  cursor: pointer; transition: border-color 0.2s, box-shadow 0.2s; outline: none;
}
.cw-select:focus {
  border-color: #F7931E; box-shadow: 0 0 0 3px rgba(247,147,30,0.12); background: #fff;
}
.cw-select-arrow {
  position: absolute; right: 14px;
  color: #94a3b8; font-size: 1rem; pointer-events: none;
}
.cw-btn-submit {
  display: inline-flex; align-items: center;
  padding: 10px 24px; border: none; border-radius: 30px;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  color: #fff; font-size: 0.88rem; font-weight: 700;
  cursor: pointer; box-shadow: 0 4px 14px rgba(247,147,30,0.35);
  transition: all 0.2s;
}
.cw-btn-submit:hover:not(:disabled) {
  background: linear-gradient(135deg, #E07B00, #C96A00);
  box-shadow: 0 6px 18px rgba(247,147,30,0.45); transform: translateY(-1px);
}
.cw-btn-submit:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }
.cw-footer {
  background: #fff; border-radius: 14px;
  padding: 18px 24px; border: 1px solid rgba(0,0,0,0.05);
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}

/* ── Page-specific ── */

/* Header icon */
.au-header-icon {
  width: 48px; height: 48px; border-radius: 14px;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 12px rgba(247,147,30,0.35); flex-shrink: 0;
}
.au-header-icon .mdi { color: #fff; font-size: 1.5rem; }

/* Card */
.au-card {
  background: #fff; border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.07);
  padding: 28px; border: 1px solid rgba(0,0,0,0.05);
}

/* Submit btn */
.au-btn-submit {
  display: inline-flex; align-items: center;
  padding: 10px 24px; border: none; border-radius: 30px;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  color: #fff; font-size: 0.88rem; font-weight: 700;
  cursor: pointer; box-shadow: 0 4px 14px rgba(247,147,30,0.35);
  transition: all 0.2s;
}
.au-btn-submit:hover:not(:disabled) {
  background: linear-gradient(135deg, #E07B00, #C96A00);
  box-shadow: 0 6px 18px rgba(247,147,30,0.45); transform: translateY(-1px);
}
.au-btn-submit:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }

/* Search */
.au-search-wrap {
  position: relative; display: flex; align-items: center;
  background: #f8fafc; border: 1.5px solid #e2e8f0;
  border-radius: 30px; height: 44px; padding: 0 16px 0 38px;
  transition: border-color 0.2s;
}
.au-search-wrap:focus-within { border-color: #F7931E; background: #fff; }
.au-search-icon { position: absolute; left: 14px; color: #94a3b8; font-size: 1rem; pointer-events: none; }
.au-search-input {
  border: none; background: transparent; outline: none;
  font-size: 0.9rem; color: #1e293b; width: 100%;
}

/* Table */
.au-table { width: 100%; border-collapse: collapse; }
.au-table thead tr { border-bottom: 2px solid #f1f5f9; }
.au-table thead th {
  padding: 10px 16px; font-size: 0.72rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.07em; color: #94a3b8; background: #f8fafc;
}
.au-table tbody tr { border-bottom: 1px solid #f1f5f9; transition: background 0.15s; }
.au-table tbody tr:hover { background: #f8fafc; }
.au-table td { padding: 14px 16px; vertical-align: middle; }

/* Badges */
.au-company-badge {
  display: inline-block; padding: 3px 10px; border-radius: 20px;
  font-size: 0.72rem; font-weight: 700; letter-spacing: 0.04em;
}

/* Contextual Banner */
.au-banner-info {
  display: flex; align-items: center; text-align: left;
  background-color: #FEF1E0; border-radius: 12px;
  padding: 18px 24px; margin-bottom: 24px;
  border-left: 4px solid #E07B00;
}
.au-banner-icon {
  font-size: 1.8rem; color: #E07B00;
  margin-right: 18px; flex-shrink: 0;
}
.au-banner-title {
  display: block; margin-bottom: 4px;
  color: #1A2238; font-size: 1.05rem; font-weight: 700;
}
.au-banner-text {
  display: block; line-height: 1.5;
  color: #334155; font-size: 0.88rem; font-weight: 500;
}
</style>
